import React, { useEffect, useRef, useState } from "react";

import LocalPrintshopOutlinedIcon from "@mui/icons-material/LocalPrintshopOutlined";
import { Box, Typography } from "@mui/material";
import { Form, Formik } from "formik";
import { useNavigate, useParams } from "react-router-dom";
import { useReactToPrint } from "react-to-print";

import {
  CreateAllergy,
  getAllergyById,
  UpdateAllergyById,
} from "../../Api/Services/AllergyServices";
import { getPetNameByWorkspaceId } from "../../Api/Services/PetServices";
import AutoComplete from "../../Components/Common/AutoComplete";
import CustomButton from "../../Components/Common/Button";
import FormGroup from "../../Components/Common/FormGroup";
import FormLayout from "../../Components/Common/FormHeader";
import InputField from "../../Components/Common/InputField";
import { showSnackbar } from "../../Components/Common/Snackbar";
import TextArea from "../../Components/Common/TextArea";
import "../Immunization/immunization.css";
import { WORKSPACEID_KEY } from "../../Constant";
import {
  FormHeaderBox,
  FormHeaderTitle,
  Body,
  Column,
  ButtonBoxStyle,
  CancelButton,
} from "../../Css";
import { AllergyFormValidation } from "../../From/Validation";
import { allergiesNamesOptions, AllergyTypes } from "../../Options";
import "../../Components/pet-information/petPrint.css";
import { useTheme } from "@mui/material/styles";

const defaultValue = {
  allergy_type: "",
  allergy_name: "",
  reaction: "",
  information: "",
  pet_id: "",
};

const AllergyForm = () => {
  const navigate = useNavigate();
  const { id } = useParams();
  const theme = useTheme();
  const componentPDF = useRef();

  // Determine if this is edit mode
  const isEditMode = !!id;

  const [initialValue, setInitialValue] = useState(defaultValue);
  const [open, setOpen] = useState(false);
  const formRef = useRef();
  const [items, setItems] = useState([]);
  const [allergyNamesOptions, setAllergyNamesOptions] = useState([]);
  const w_id = localStorage.getItem(WORKSPACEID_KEY);

  // Load existing data for edit mode
  const loadAllergyDetails = async () => {
    if (!isEditMode) return;
    
    try {
      const { data } = await getAllergyById(id);
      setInitialValue({
        ...initialValue,
        allergy_type: data?.data?.[0]?.allergy_type,
        allergy_name: data?.data?.[0]?.allergy_name,
        reaction: data?.data?.[0]?.reaction,
        information: data?.data?.[0]?.information,
        pet_id: data?.data?.[0]?.pet_id,
      });
      handleAllergyTypeChange("", { value: data?.data?.[0]?.allergy_type });
    } catch (error) {
      console.error("error: ", error);
      showSnackbar({
        type: "error",
        message: "Failed to load allergy details.",
      });
    }
  };

  const getPetNames = async () => {
    try {
      const { data } = await getPetNameByWorkspaceId(w_id);
      const filteredData = data?.data?.map((item) => {
        return {
          value: item?.id,
          label: item?.name,
        };
      });
      setItems(filteredData);
    } catch (error) {
      console.error("error: ", error);
    }
  };

  useEffect(() => {
    getPetNames();
    if (isEditMode) {
      loadAllergyDetails();
    }
  }, [w_id, isEditMode]);

  // Create new allergy
  const createAllergyData = async (values) => {
    const value = {
      allergy_type: values?.allergy_type,
      allergy_name: values?.allergy_name,
      reaction: values?.reaction,
      information: values?.information,
      pet_id: values?.pet_id,
    };
    
    try {
      const AllergyRes = await CreateAllergy(value);
      if (AllergyRes?.data?.success) {
        showSnackbar({
          type: "success",
          message: "Allergy information added successfully",
        });
      }
    } catch (err) {
      showSnackbar({
        type: "error",
        message: "Allergy information added failed",
      });
      console.error(err);
      throw err;
    }
  };

  // Update existing allergy
  const updateAllergyData = async (values) => {
    const value = {
      allergy_type: values?.allergy_type,
      allergy_name: values?.allergy_name,
      reaction: values?.reaction,
      information: values?.information,
      pet_id: values?.pet_id,
    };
    
    try {
      const res = await UpdateAllergyById(id, value);
      if (res?.data?.success) {
        showSnackbar({
          type: "success",
          message: "Allergy information updated successfully",
        });
      }
    } catch (error) {
      showSnackbar({
        type: "error",
        message: "Allergy information update failed",
      });
      console.error("error: ", error);
      throw error;
    }
  };

  const handleSubmit = async (values, { isSubmitting }) => {
    try {
      if (isEditMode) {
        await updateAllergyData(values);
      } else {
        await createAllergyData(values);
      }
      setOpen(true);
    } catch (error) {
      console.error("Failed to process allergy:", error);
    } finally {
      isSubmitting(false);
    }
  };

  const handleAllergyTypeChange = (e, newValue) => {
    const selectedType = allergiesNamesOptions?.filter((item) => item.type === newValue?.value);
    const filteredAllergy = selectedType?.[0]?.names?.map((item) => {
      return {
        value: item,
        label: item,
      };
    });
    filteredAllergy.push({ value: "Other", label: "Other" });
    setAllergyNamesOptions(filteredAllergy);
    formRef?.current?.setFieldValue("allergy_name", "");
  };

  // Handle print functionality
  const handlePrint = useReactToPrint({
    content: () => componentPDF.current,
    documentTitle: "Allergy Information",
    pageStyle: `
      @page { size: A3; margin: 200px; }
      body { margin: 100px; }
      .print-text-field { font-size: 14px; }
    `,
  });

  return (
    <FormLayout title={isEditMode ? "Edit Allergy Information" : "Allergy Information"}>
      <Formik
        initialValues={initialValue}
        onSubmit={handleSubmit}
        validationSchema={AllergyFormValidation}
        enableReinitialize
        innerRef={formRef}
      >
        {({ getFieldProps, values, isSubmitting }) => (
          <Form>
            <Box sx={FormHeaderBox(theme.palette.primary.main)}>
              <Box sx={{ borderBottom: `1px solid ${theme.palette.primary.main}`, width: "100%" }}>
                <Typography variant="h6" sx={FormHeaderTitle}>
                  Allergy Details 
                </Typography>
              </Box>
              <div className="scroll2" ref={componentPDF} style={{ width: "100%" }}>
                <Body style={{ gap: "10px 20px" }}>
                  <Column>
                    <FormGroup name="pet_id" label={"Pet Name"} requiredField>
                      <AutoComplete
                        {...getFieldProps("pet_id")}
                        options={items}
                        value={isEditMode ? items.find(option => option.value === values.pet_id) : undefined}
                        textFieldStyle={{
                          width: {
                            xs: 250,
                            sm: 443,
                            md: 443,
                            lg: 443,
                            xl: 443,
                          },
                          height: "35px",
                        }}
                      />
                    </FormGroup>
                  </Column>
                  <Column>
                    <FormGroup
                      name="allergy_type"
                      label={"Allergy Type"}
                      requiredField
                    >
                      <AutoComplete
                        {...getFieldProps("allergy_type")}
                        options={AllergyTypes}
                        otherType="Other"
                        placeholder="Select a type"
                        onChange={handleAllergyTypeChange}
                        isOnChange
                        value={isEditMode ? AllergyTypes.find(option => option.value === values.allergy_type) : undefined}
                      />
                    </FormGroup>
                  </Column>
                  {values?.allergy_type && (
                    <Column>
                      <FormGroup name="allergy_name" label={"Allergy Name"}>
                        <AutoComplete
                          {...getFieldProps("allergy_name")}
                          options={allergyNamesOptions}
                          otherType="Other"
                          placeholder="Select a Allergy name"
                          value={isEditMode ? allergyNamesOptions.find(option => option.value === values.allergy_name) : undefined}
                        />
                      </FormGroup>
                    </Column>
                  )}
                  <Column>
                    <FormGroup name="reaction" label={"Reaction"}>
                      <InputField {...getFieldProps("reaction")} />
                    </FormGroup>
                  </Column>
                </Body>
                <Body style={{ gap: "10px 20px", marginTop: "10px" }}>
                  <Column>
                    <FormGroup name="information" label={"Information"}>
                      <TextArea
                        maxLength={300}
                        rows={5}
                        {...getFieldProps("information")}
                      />
                    </FormGroup>
                  </Column>
                </Body>
              </div>
              <Box sx={ButtonBoxStyle(`${theme.palette.primary.main}`)}>
                <Box display="flex">
                  <CustomButton
                    label="Cancel"
                    type="button"
                    style={CancelButton(`${theme.palette.primary.main}`)}
                    onClick={() => navigate(-1)}
                  />
                  <CustomButton
                    label={isSubmitting ? (isEditMode ? "Saving..." : "Submitting...") : (isEditMode ? "Save" : "Submit")}
                    disabled={isSubmitting}
                    type="submit"
                  />
                  {isEditMode && (
                    <CustomButton
                      startIcon={<LocalPrintshopOutlinedIcon />}
                      onClick={handlePrint}
                    />
                  )}
                </Box>
              </Box>
            </Box>
          </Form>
        )}
      </Formik>
    </FormLayout>
  );
};

export default AllergyForm;
